import ldIsNull from "lodash/isNull";
import ldLast from "lodash/last";
import ldOmitBy from "lodash/omitBy";
import ldPick from "lodash/pick";
import ldRange from "lodash/range";

import { DeliveryTypeValues, FormatValues } from "../Activity";
import * as ActivitySubjects from "../ActivitySubjects";

import type { SearchFilters } from "@outschool/gql-frontend-generated";

export enum SearchToggleType {
  ALL = "all",
  TUTORING = "tutoring",
  CAMP = "camp",
  SELF_PACED = "self-paced",
  COURSES = "courses",
  CLUBS = "clubs",
  ONE_TIME = "one-time",
  INCLUDED_WITH_MEMBERSHIP = "free-self-paced",
}

type PriceOption = { priceMin?: number; priceMax?: number };

const getPriceOptions = (prices: number[]): PriceOption[] => {
  return [
    {},
    ...prices.map(price => ({
      priceMax: price,
    })),
    {
      priceMin: ldLast(prices),
    },
  ];
};

export const PRICE_OPTIONS: Record<SearchToggleType, PriceOption[]> = {
  [SearchToggleType.ALL]: getPriceOptions(ldRange(10, 50, 10)),
  [SearchToggleType.TUTORING]: getPriceOptions(ldRange(30, 70, 10)),
  [SearchToggleType.SELF_PACED]: getPriceOptions(ldRange(10, 50, 10)),
  [SearchToggleType.CAMP]: getPriceOptions(ldRange(10, 50, 10)),
  [SearchToggleType.COURSES]: getPriceOptions(ldRange(50, 450, 100)),
  [SearchToggleType.CLUBS]: getPriceOptions(ldRange(10, 50, 10)),
  [SearchToggleType.ONE_TIME]: getPriceOptions(ldRange(20, 60, 10)),
  [SearchToggleType.INCLUDED_WITH_MEMBERSHIP]: getPriceOptions(
    ldRange(10, 50, 10)
  ),
};

export const SEARCH_TOGGLE_TO_FILTERS: Record<SearchToggleType, SearchFilters> =
  {
    [SearchToggleType.ALL]: {},
    [SearchToggleType.TUTORING]: { capacityMax: 1, format: FormatValues.Live },
    [SearchToggleType.CAMP]: {
      capacityMin: 2,
      format: FormatValues.Live,
      weeklyMeetingsMin: 2,
      durationWeeksMin: 1,
      durationWeeksMax: 4,
      startBefore: "2024-09-01",
    },
    [SearchToggleType.SELF_PACED]: {
      format: FormatValues.SelfPaced,
    },
    [SearchToggleType.COURSES]: {
      delivery: DeliveryTypeValues.Course,
    },
    [SearchToggleType.CLUBS]: { delivery: DeliveryTypeValues.EnrichmentOrClub },
    [SearchToggleType.ONE_TIME]: {
      capacityMin: 2,
      delivery: DeliveryTypeValues.OneTimeClass,
    },
    [SearchToggleType.INCLUDED_WITH_MEMBERSHIP]: {
      format: FormatValues.SelfPaced,
      includedWithMembership: true,
    },
  };

const TUTORING_TOGGLE_SUBJECT_ORDER = [
  7, 3, 9, 12, 0, 6, 8, 1, 10, 4, 2, 11, 5,
];
const COURSE_TOGGLE_SUBJECT_ORDER = [3, 7, 9, 10, 12, 2, 1, 0, 6, 8, 5, 4, 11];

export const getSubjectForSearchToggle = (searchToggle: SearchToggleType) => {
  const order =
    searchToggle === SearchToggleType.TUTORING
      ? TUTORING_TOGGLE_SUBJECT_ORDER
      : searchToggle === SearchToggleType.COURSES
      ? COURSE_TOGGLE_SUBJECT_ORDER
      : undefined;

  if (order) {
    return order
      .map(i => ActivitySubjects.Subjects[i])
      .filter(theme => !!theme);
  }

  return ActivitySubjects.Subjects;
};

const allSearchToggleFilterKeys = [
  ...new Set(
    Object.values(SEARCH_TOGGLE_TO_FILTERS).flatMap(obj => Object.keys(obj))
  ),
];

// Since the filters change depending on which toggle button is used, clear out
// the irrelevant filters.
const priceFiltersToReset = {
  pricePerMeetingMax: null,
  pricePerMeetingMin: null,
  pricePerCourseMax: null,
  pricePerCourseMin: null,
};

const priceCreditsFiltersToReset = {
  priceCreditsPerMeetingMax: null,
  priceCreditsPerMeetingMin: null,
  priceCreditsPerCourseMax: null,
  priceCreditsPerCourseMin: null,
};
const evaluationFiltersToReset = {
  hasHomework: null,
  hasAssessment: null,
  hasGrades: null,
};

export function filtersToResetBy(
  activeSearchToggle: SearchToggleType
): SearchFilters {
  switch (activeSearchToggle) {
    case SearchToggleType.TUTORING:
      return {
        delivery: null,
        durationWeeksMax: null,
        durationWeeksMin: null,
        weeklyMeetingsMin: null,
        startBefore: null,
        capacityMin: null,
        includedWithMembership: null,
        ...priceFiltersToReset,
        ...priceCreditsFiltersToReset,
        ...evaluationFiltersToReset,
      };
    case SearchToggleType.COURSES:
      return {
        durationWeeksMax: null,
        durationWeeksMin: null,
        weeklyMeetingsMin: null,
        startBefore: null,
        capacityMin: null,
        format: null,
        includedWithMembership: null,
        ...priceFiltersToReset,
        ...priceCreditsFiltersToReset,
      };
    case SearchToggleType.SELF_PACED:
      return {
        delivery: null,
        durationWeeksMax: null,
        durationWeeksMin: null,
        weeklyMeetingsMin: null,
        startBefore: null,
        capacityMin: null,
        includedWithMembership: null,
        ...priceFiltersToReset,
        ...priceCreditsFiltersToReset,
      };
    case SearchToggleType.CLUBS:
      return {
        durationWeeksMax: null,
        durationWeeksMin: null,
        weeklyMeetingsMin: null,
        startBefore: null,
        capacityMin: null,
        format: null,
        includedWithMembership: null,
        ...priceFiltersToReset,
        ...priceCreditsFiltersToReset,
        ...evaluationFiltersToReset,
      };
    case SearchToggleType.INCLUDED_WITH_MEMBERSHIP:
      return {
        durationWeeksMax: null,
        durationWeeksMin: null,
        weeklyMeetingsMin: null,
        startBefore: null,
        capacityMin: null,
        ...priceFiltersToReset,
        ...priceCreditsFiltersToReset,
        ...evaluationFiltersToReset,
      };
    case SearchToggleType.ONE_TIME:
      return {
        durationWeeksMax: null,
        durationWeeksMin: null,
        weeklyMeetingsMin: null,
        startBefore: null,
        capacityMin: null,
        format: null,
        includedWithMembership: null,
        ...priceFiltersToReset,
        ...priceCreditsFiltersToReset,
        ...evaluationFiltersToReset,
      };
    case SearchToggleType.CAMP:
      return {
        ...priceFiltersToReset,
        ...priceCreditsFiltersToReset,
      };
    case SearchToggleType.ALL:
      return {
        ...priceFiltersToReset,
        ...priceCreditsFiltersToReset,
        ...allSearchToggleFilterKeys.reduce((obj, key) => {
          obj[key] = null;
          return obj;
        }, {} as Record<string, null>),
      };
  }
}

export function getToggleButtonFilters(searchFilters?: SearchFilters) {
  if (!searchFilters) {
    return {};
  }
  return ldOmitBy(ldPick(searchFilters, allSearchToggleFilterKeys), ldIsNull);
}

// From value, determine which toggle button is selected. Because the combinations of
// value cases are not mutually exclusive, this function will enforce a priority order.
// We never want to show more than one toggle button as selected.
// NOTE: This is not a reverse map of Search.SEARCH_TOGGLE_TO_FILTERS, this is more forgiving.
export function activeSearchToggleFromFilters(searchFilters: SearchFilters) {
  if (
    searchFilters.capacityMax === 1 &&
    searchFilters.format?.includes(FormatValues.Live)
  ) {
    return SearchToggleType.TUTORING;
  }
  if (
    (searchFilters.delivery?.includes(DeliveryTypeValues.SemesterCourse) &&
      searchFilters.delivery?.includes(DeliveryTypeValues.ShortCourse)) ||
    searchFilters.delivery?.includes(DeliveryTypeValues.Course)
  ) {
    return SearchToggleType.COURSES;
  }
  if (
    searchFilters.includedWithMembership === true &&
    searchFilters.format === FormatValues.SelfPaced
  ) {
    return SearchToggleType.INCLUDED_WITH_MEMBERSHIP;
  }
  if (searchFilters.format?.includes(FormatValues.SelfPaced)) {
    return SearchToggleType.SELF_PACED;
  }
  if (
    searchFilters.delivery?.includes(DeliveryTypeValues.OngoingClass) ||
    searchFilters.delivery?.includes(DeliveryTypeValues.EnrichmentOrClub)
  ) {
    return SearchToggleType.CLUBS;
  }
  if (searchFilters.delivery?.includes(DeliveryTypeValues.OneTimeClass)) {
    return SearchToggleType.ONE_TIME;
  }
  if (
    searchFilters.capacityMin === 2 &&
    searchFilters.format?.includes(FormatValues.Live) &&
    searchFilters.durationWeeksMin === 1 &&
    searchFilters.durationWeeksMax === 4 &&
    searchFilters.weeklyMeetingsMin === 2
  ) {
    return SearchToggleType.CAMP;
  }
  return SearchToggleType.ALL;
}

// SearchToggle (Tabs) can change being clicked on or by specific filters being selected,
// make sure to reset some filters when they do.
export function newFiltersFromSearchToggleChange(
  currentSearchToggle: SearchToggleType,
  searchFilters: SearchFilters
) {
  const newSearchToggle = activeSearchToggleFromFilters(searchFilters);

  // Persist the current filters if the search toggle is not changing.
  if (currentSearchToggle === newSearchToggle) {
    return searchFilters;
  }

  // Some filters need to be reset when switching between search toggles types since they do not appear as an option.
  const filtersToReset = filtersToResetBy(newSearchToggle);

  // New filter values
  return {
    capacityMin: null,
    capacityMax: null,
    delivery: null,
    format: null,
    ...searchFilters,
    ...filtersToReset,
  };
}

export function useContextualFilters({
  activeSearchToggle,
  isMobile,
}: {
  activeSearchToggle: SearchToggleType;
  isMobile: boolean;
}) {
  const showChatGroupInsideClassType =
    activeSearchToggle !== SearchToggleType.COURSES;

  const showClassSizeFilter = [
    SearchToggleType.ALL,
    SearchToggleType.COURSES,
    SearchToggleType.TUTORING,
    SearchToggleType.CAMP,
  ].includes(activeSearchToggle);

  const showClassTypeFilterInUncommonFilters = [
    SearchToggleType.ALL,
    SearchToggleType.COURSES,
    SearchToggleType.CLUBS,
    SearchToggleType.SELF_PACED,
    SearchToggleType.INCLUDED_WITH_MEMBERSHIP,
  ].includes(activeSearchToggle);

  const showCurriculums = [
    SearchToggleType.ALL,
    SearchToggleType.TUTORING,
    SearchToggleType.CAMP,
    SearchToggleType.SELF_PACED,
    SearchToggleType.INCLUDED_WITH_MEMBERSHIP,
    SearchToggleType.COURSES,
  ].includes(activeSearchToggle);

  const showEvaluation = [
    SearchToggleType.ALL,
    SearchToggleType.COURSES,
    SearchToggleType.CAMP,
    SearchToggleType.SELF_PACED,
    SearchToggleType.INCLUDED_WITH_MEMBERSHIP,
  ].includes(activeSearchToggle);

  const showGradeFilterOnMainBar =
    isMobile ||
    [
      SearchToggleType.ALL,
      SearchToggleType.COURSES,
      SearchToggleType.TUTORING,
      SearchToggleType.SELF_PACED,
      SearchToggleType.INCLUDED_WITH_MEMBERSHIP,
    ].includes(activeSearchToggle);

  const showStandards = [
    SearchToggleType.ALL,
    SearchToggleType.TUTORING,
    SearchToggleType.CAMP,
    SearchToggleType.SELF_PACED,
    SearchToggleType.INCLUDED_WITH_MEMBERSHIP,
    SearchToggleType.COURSES,
  ].includes(activeSearchToggle);

  return {
    showChatGroupInsideClassType,
    showClassSizeFilter,
    showClassTypeFilterInUncommonFilters,
    showCurriculums,
    showEvaluation,
    showGradeFilterOnMainBar,
    showStandards,
  };
}
